/* ==========================================================================
   Slider block (_bc-slider) — Flickity carousel of child theme blocks.

   Mirrors the Cuyana Slider section (CuyanaSliderSection in theme.js), but runs
   per block instead of per section, since sections.register can't target a
   block. Desktop overrides live in config.breakpoints and are merged here;
   Flickity itself ignores that key.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 768px)');

  function Slider(root) {
    this.root = root;
    this.carousel = root.querySelector('[data-flickity-config]');
    this.progressCount = root.querySelector('.progress-count');
    this.config = JSON.parse(this.carousel.getAttribute('data-flickity-config') || '{}');

    this._onBreakpoint = this._rebuild.bind(this);
    this._onMediaLoad = this._scheduleResize.bind(this);

    if (DESKTOP.addEventListener) {
      DESKTOP.addEventListener('change', this._onBreakpoint);
    } else {
      DESKTOP.addListener(this._onBreakpoint);
    }
    // Lazy-loaded tile images change cell heights after Flickity has measured them.
    // 'load' doesn't bubble, so it has to be captured.
    this.carousel.addEventListener('load', this._onMediaLoad, true);
    this.carousel.addEventListener('lazyloaded', this._onMediaLoad);
    window.addEventListener('load', this._onMediaLoad);

    this._build();
    root.__cuySlider = this;
  }

  Slider.prototype._resolveConfig = function () {
    var config = Object.assign({}, this.config);
    delete config.breakpoints;
    (this.config.breakpoints || []).forEach(function (bp) {
      if (window.matchMedia(bp.matches).matches) Object.assign(config, bp.settings);
    });
    return config;
  };

  Slider.prototype._build = function () {
    var _this = this;

    // Center-focus styles in theme.css target .slide; child blocks don't carry it.
    Array.prototype.forEach.call(this.carousel.children, function (cell) {
      cell.classList.add('slide');
    });

    // navStyle is ours, not Flickity's: show-dots/show-lines style the page dots that
    // theme.css draws, and mobile and desktop can each pick a different one.
    var config = this._resolveConfig();
    this.carousel.classList.toggle('show-dots', config.navStyle === 'dots');
    this.carousel.classList.toggle('show-lines', config.navStyle === 'lines');
    delete config.navStyle;

    this.flickity = new Flickity(this.carousel, config);

    // Clicking a partially visible slide brings it into view instead of following its link.
    this.flickity.on('staticClick', function (event, pointer, cellElement, cellIndex) {
      if (typeof cellIndex !== 'number') return;
      // Fully visible slides keep their links, so tiles in a multi-slide row stay clickable.
      if (isFullyVisible(cellElement, _this.flickity.viewport)) return;
      var link = cellElement.querySelector('a');
      if (link) link.addEventListener('click', preventLink, { once: true });
      _this.flickity.select(cellIndex);
    });

    if (this.progressCount) {
      this.flickity.on('select', function () {
        _this.progressCount.textContent = _this.flickity.selectedIndex + 1;
      });
    }
  };

  Slider.prototype._rebuild = function () {
    if (this.flickity) this.flickity.destroy();
    this._build();
  };

  Slider.prototype._scheduleResize = function () {
    var _this = this;
    if (this._resizeRaf) return;
    this._resizeRaf = requestAnimationFrame(function () {
      _this._resizeRaf = 0;
      if (_this.flickity) _this.flickity.resize();
    });
  };

  Slider.prototype.selectBlock = function (blockEl) {
    if (!this.flickity) return;
    var index = this.flickity.cells.findIndex(function (cell) {
      return cell.element === blockEl || cell.element.contains(blockEl);
    });
    if (index > -1) this.flickity.select(index, false, true);
    this.flickity.pausePlayer();
  };

  Slider.prototype.destroy = function () {
    if (DESKTOP.removeEventListener) {
      DESKTOP.removeEventListener('change', this._onBreakpoint);
    } else {
      DESKTOP.removeListener(this._onBreakpoint);
    }
    this.carousel.removeEventListener('load', this._onMediaLoad, true);
    this.carousel.removeEventListener('lazyloaded', this._onMediaLoad);
    window.removeEventListener('load', this._onMediaLoad);
    if (this.flickity) this.flickity.destroy();
    delete this.root.__cuySlider;
  };

  // 1px tolerance absorbs sub-pixel slide widths (e.g. 100% / 4.3).
  function isFullyVisible(cellElement, viewport) {
    var cell = cellElement.getBoundingClientRect();
    var view = viewport.getBoundingClientRect();
    return cell.left >= view.left - 1 && cell.right <= view.right + 1;
  }

  function preventLink(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  function initWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-cuy-slider]');
    Array.prototype.forEach.call(roots, function (root) {
      if (!root.__cuySlider) new Slider(root);
    });
  }

  function destroyWithin(scope) {
    var roots = (scope || document).querySelectorAll('[data-cuy-slider]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__cuySlider) root.__cuySlider.destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initWithin(document);
    });
  } else {
    initWithin(document);
  }

  // Shopify theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) {
    initWithin(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyWithin(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-cuy-slider]');
    if (root && root !== e.target && root.__cuySlider) root.__cuySlider.selectBlock(e.target);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var root = e.target.closest('[data-cuy-slider]');
    if (root && root.__cuySlider && root.__cuySlider.flickity) root.__cuySlider.flickity.unpausePlayer();
  });

  window.CuySlider = { init: initWithin, destroy: destroyWithin };
})();
